import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Button from '../ui/Button';

// Questions grouped into the sections listed in the sticky index rail.
const groups = [
    {
        id: 'getting-started',
        title: 'Getting started',
        items: [
            {
                q: 'What is Pixiy and where do the images come from?',
                a: 'Pixiy is a free image, illustration and video gallery. All media is served through the Pixabay API, giving you access to millions of high‑quality, royalty‑free visuals contributed by creators around the world.',
            },
            {
                q: 'Are the photos and videos free to use?',
                a: 'Yes. Everything you find here is provided under the Pixabay Content License, which is free for personal and commercial use. Attribution is appreciated but not required. We always recommend reviewing Pixabay’s license for the specific edge cases (like using identifiable people or brands).',
            },
            {
                q: 'Do I need an account?',
                a: 'Browsing, searching and downloading work without an account. Signing in unlocks personal features like Favorites and Collections so your saved items are remembered across visits.',
            },
        ],
    },
    {
        id: 'finding-downloading',
        title: 'Finding & downloading',
        items: [
            {
                q: 'How do I search and filter results?',
                a: 'Type a keyword into the search bar on the Photos, Illustrations or Videos pages. You can then refine results with the filter row: Orientation, Size (minimum resolution), Color, Published order (Popular or Latest), and a SafeSearch toggle to hide potentially unsafe content.',
            },
            {
                q: 'How do I download a photo or video?',
                a: 'Hover over any card and use the download action. Photos download at the largest available resolution, and videos download the preview clip. Downloads happen straight from Pixabay’s servers — no sign‑up required.',
            },
            {
                q: 'Can I edit an image before downloading?',
                a: 'Yes. Use the edit action on any photo card to open the built‑in Image Editor, where you can crop, draw, add text and shapes, apply filters, and more, then export your result.',
            },
        ],
    },
    {
        id: 'saving-personalizing',
        title: 'Saving & personalizing',
        items: [
            {
                q: 'What are Favorites and Collections?',
                a: 'Favorites let you quickly save individual items you love with a single click. Collections let you group saved media into named sets so you can organize inspiration by project or theme. Your saved items are tied to your account.',
            },
            {
                q: 'Does Pixiy have a dark mode?',
                a: 'It does. Use the theme toggle in the navigation bar to switch between light and dark. Your preference is saved in your browser and restored automatically on your next visit.',
            },
        ],
    },
    {
        id: 'troubleshooting',
        title: 'Troubleshooting',
        items: [
            {
                q: 'Why did an image or video fail to load?',
                a: 'Occasionally a media URL from the source is temporarily unavailable. When that happens we show a soft placeholder instead of a broken icon. Refreshing the page or trying a new search usually resolves it. If search returns nothing at all, double‑check the Pixabay API key configuration.',
            },
        ],
    },
];

const pad = (n) => String(n + 1).padStart(2, '0');

const FaqRow = ({ item, index, isOpen, onToggle }) => {
    const panelId = `faq-panel-${index}`;
    const buttonId = `faq-button-${index}`;

    return (
        <div className="border-t border-gray-200 dark:border-gray-800 first:border-t-0">
            <h3>
                <button
                    id={buttonId}
                    type="button"
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="group w-full flex items-baseline gap-4 sm:gap-6 text-left py-5 rounded-xl"
                >
                    {/* index numeral — subdued until its answer is open */}
                    <span
                        className={`font-serif text-xl sm:text-2xl tabular-nums shrink-0 transition-colors duration-300 ${
                            isOpen
                                ? 'text-brand-500'
                                : 'text-gray-300 dark:text-gray-700 group-hover:text-brand-400'
                        }`}
                    >
                        {pad(index)}
                    </span>
                    <span className="flex-1 text-base sm:text-lg font-bold text-gray-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                        {item.q}
                    </span>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`shrink-0 self-center text-sm text-gray-400 group-hover:text-brand-500 transition-all duration-300 ${
                            isOpen ? 'rotate-180 text-brand-500' : ''
                        }`}
                    />
                </button>
            </h3>

            {/* grid-rows trick animates to the answer's natural height */}
            <div
                className={`grid transition-all duration-300 ease-in-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
            >
                <div className="overflow-hidden">
                    <div
                        id={panelId}
                        role="region"
                        aria-labelledby={buttonId}
                        className="pb-6 pl-10 sm:pl-14 pr-8"
                    >
                        {/* accent rule ties the answer back to the open numeral */}
                        <p className="border-l-2 border-brand-400 pl-4 sm:pl-5 text-gray-500 dark:text-gray-400 leading-relaxed">
                            {item.a}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FAQ = () => {
    const [openKey, setOpenKey] = useState('0-0');
    const [activeGroup, setActiveGroup] = useState(groups[0].id);
    const sectionRefs = useRef({});

    // Running 01..09 numbering across groups, so the list reads as one index.
    const numbering = useMemo(() => {
        let n = 0;
        return groups.map((g) => g.items.map(() => n++));
    }, []);

    // Highlight the rail link for whichever section is currently in view.
    useEffect(() => {
        const onScroll = () => {
            const doc = document.documentElement;

            // The last section is short and sits at the end of the page, so it can
            // never reach the reading line — the page runs out of scroll first.
            // Clamp to it once we're at the bottom, or it could never activate.
            if (window.innerHeight + window.scrollY >= doc.scrollHeight - 2) {
                setActiveGroup(groups[groups.length - 1].id);
                return;
            }

            // Otherwise: the last section whose top has passed a reading line
            // near the top of the viewport.
            const line = window.innerHeight * 0.2;
            let current = groups[0].id;
            groups.forEach((g) => {
                const el = sectionRefs.current[g.id];
                if (el && el.getBoundingClientRect().top <= line) current = g.id;
            });
            setActiveGroup(current);
        };

        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid lg:grid-cols-[19rem,1fr] gap-10 lg:gap-16 items-start">
            {/* ===== left rail — title + index (sticky on desktop) =====
                min-w-0: as a grid item this defaults to min-width:auto, which
                lets the index chips force the whole column wider than the
                viewport on mobile. */}
            <aside className="min-w-0 lg:sticky lg:top-24">
                <h1 className="font-serif text-4xl sm:text-5xl font-medium leading-tight text-gray-900 dark:text-white">
                    Frequently asked{' '}
                    <span className="relative whitespace-nowrap text-brand-500 dark:text-brand-400">
                        questions
                        <svg
                            className="absolute left-0 w-full"
                            style={{ bottom: '-0.4rem' }}
                            height="14"
                            viewBox="0 0 200 14"
                            preserveAspectRatio="none"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M3 9 C 45 3, 80 13, 120 7 S 175 3, 197 8"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                        </svg>
                    </span>
                </h1>
                <p className="mt-6 text-gray-500 dark:text-gray-400 leading-relaxed">
                    Everything you need to know about finding, saving and using free visuals on Pixiy.
                </p>

                {/* index — wrapping chips on mobile, vertical list on desktop */}
                <nav aria-label="Jump to section" className="mt-8">
                    <ul className="flex flex-wrap lg:flex-col gap-2 lg:gap-0">
                        {groups.map((g, gi) => {
                            const isActive = activeGroup === g.id;
                            return (
                                <li key={g.id}>
                                    <a
                                        href={`#${g.id}`}
                                        aria-current={isActive ? 'true' : undefined}
                                        className={`flex items-center gap-3 whitespace-nowrap rounded-full lg:rounded-lg border lg:border-0 lg:border-l-2 px-4 lg:px-0 lg:pl-4 py-2 lg:py-2.5 text-sm font-semibold transition-all duration-200 ${
                                            isActive
                                                ? 'border-brand-400 lg:border-brand-500 text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10 lg:bg-transparent dark:lg:bg-transparent'
                                                : 'border-gray-200 dark:border-gray-800 lg:border-gray-200 dark:lg:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white lg:hover:border-gray-400'
                                        }`}
                                    >
                                        <span className="font-serif tabular-nums text-xs opacity-60">
                                            {pad(gi)}
                                        </span>
                                        {g.title}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* still stuck — rides along in the sticky rail */}
                <div className="hidden lg:block mt-8 rounded-3xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-6">
                    <h2 className="font-bold text-gray-900 dark:text-white">Still have a question?</h2>
                    <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        Our guides go deeper on working with free visuals.
                    </p>
                    <Link
                        to="/blog"
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-600 dark:text-brand-400 hover:gap-2.5 transition-all"
                    >
                        Visit the blog <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                    </Link>
                </div>
            </aside>

            {/* ===== right column — grouped Q&A =====
                lg:pb-40 is load-bearing, not just air: without trailing scroll
                room the last sections never reach the reading line above, so
                their rail links could never highlight. */}
            <div className="min-w-0 lg:pb-40">
                {groups.map((group, gi) => (
                    <section
                        key={group.id}
                        id={group.id}
                        ref={(el) => { sectionRefs.current[group.id] = el; }}
                        className="scroll-mt-24 mb-12 last:mb-0"
                    >
                        {/* section header — small caps label with a trailing rule */}
                        <div className="flex items-center gap-4 mb-2">
                            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600 dark:text-brand-400">
                                {group.title}
                            </h2>
                            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
                            <span className="font-serif text-xs tabular-nums text-gray-400 dark:text-gray-600">
                                {group.items.length}
                            </span>
                        </div>

                        {group.items.map((item, ii) => {
                            const key = `${gi}-${ii}`;
                            return (
                                <FaqRow
                                    key={item.q}
                                    item={item}
                                    index={numbering[gi][ii]}
                                    isOpen={openKey === key}
                                    onToggle={() => setOpenKey(openKey === key ? null : key)}
                                />
                            );
                        })}
                    </section>
                ))}

                {/* mobile-only counterpart to the rail's card */}
                <div className="lg:hidden mt-12 rounded-3xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-8 text-center">
                    <h2 className="text-xl font-extrabold text-gray-900 dark:text-white">
                        Still have a question?
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-2">
                        Browse our guides and articles for more tips on working with free visuals.
                    </p>
                    <Button to="/blog" size="lg" className="mt-5 font-bold">
                        Visit the Blog <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FAQ;
